/* eslint-disable lingui/no-unlocalized-strings */
import { t } from "@lingui/macro";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import Axios from "axios";
import dayjs from "dayjs";
import { addDays, addMonths, addWeeks, isAfter } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/client/services/user";
import { sourceTimeZone, studentScheduleWall } from "@/client/libs/student-schedule";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy:        "#102D4D",
  navyLight:   "#1a3f6b",
  assessment:  "#1d4ed8",
  interview:   "#6d28d9",
  placement:   "#15803d",
  event:       "#b45309",
  surface:     "#f8fafc",
  border:      "#e2e8f0",
  borderBlue:  "#dbeafe",
};

const EVENT_THEME: Record<string, { soft: string; text: string; border: string }> = {
  placement:  { soft: "#f0fdf4", text: C.placement,  border: "#bbf7d0" },
  assessment: { soft: "#eff6ff", text: C.assessment,  border: "#bfdbfe" },
  interview:  { soft: "#f5f3ff", text: C.interview,   border: "#ddd6fe" },
  event:      { soft: "#fffbeb", text: C.event,       border: "#fde68a" },
};

const getEventTheme = (type?: string) => EVENT_THEME[type || "event"] || EVENT_THEME.event;

const getTypeIcon = (type?: string, size = 11) => {
  const sx = { fontSize: size };
  switch (type) {
    case "assessment": return <AssignmentOutlinedIcon sx={sx} />;
    case "interview":  return <MicNoneOutlinedIcon sx={sx} />;
    case "placement":  return <BusinessCenterOutlinedIcon sx={sx} />;
    default:           return <EventOutlinedIcon sx={sx} />;
  }
};

// ─── Types ────────────────────────────────────────────────────────────────────
type CalendarItem = {
  title: string; start: string; end: string;
  startTime?: string; endTime?: string;
  type: "event" | "assessment" | "interview" | "placement";
  extendedProps: {
    status?: string; description?: string; company?: string;
    salary?: number; code?: string; questionCount?: number;
    score?: number; totalScore?: number; placementType?: string;
    eventId?: string; isRecurring?: boolean; instanceNumber?: number; priority?: number;
  };
};

type NotificationItem = { title: string; description: string; status: string; created_at: string };

// ─── Styled components ────────────────────────────────────────────────────────
const PageShell = styled(Box)(() => ({
  padding: "14px 0px",
  minHeight: "100%",
  maxWidth: "100%",
}));

const SectionCard = styled(Paper)(() => ({
  borderRadius: 12,
  border: `1px solid ${C.borderBlue}`,
  boxShadow: "0 1px 6px rgba(15,23,42,0.06)",
  background: alpha("#ffffff", 0.95),
  overflow: "hidden",
}));

// ─── Recurring event generator (unchanged logic) ──────────────────────────────
const generateRecurringEvents = (event: any) => {
  const events: any[] = [];
  if (event.frequency === "once") {
    const zone = sourceTimeZone(event.schedule_time_zone);
    const eventDate = (value: string) => value.includes("T")
      ? dayjs(value).tz(zone).format("YYYY-MM-DD")
      : value.slice(0, 10);
    events.push({
      title: event.event_name, start: eventDate(event.start_date),
      end: eventDate(event.end_date),
      startTime: event.start_time, endTime: event.end_time,
      startAt: event.start_at, endAt: event.end_at, scheduleZone: event.schedule_time_zone,
      eventId: event.id, isRecurring: false,
    });
    return events;
  }
  const scheduleZone = sourceTimeZone(event.schedule_time_zone);
  const sourceDate = (value: string) => value.includes("T")
    ? dayjs(value).tz(scheduleZone).format("YYYY-MM-DD")
    : value.slice(0, 10);
  const masterStart = new Date(`${sourceDate(event.start_date)}T12:00:00Z`);
  const masterEnd   = new Date(`${sourceDate(event.end_date)}T12:00:00Z`);
  const recEnd      = event.recurrence_end_date ? new Date(`${sourceDate(event.recurrence_end_date)}T12:00:00Z`) : null;
  const count       = event.recurrence_count;
  const interval    = event.recurrence_interval || 1;
  const durationMs  = masterEnd.getTime() - masterStart.getTime();
  let cur = masterStart;
  let n = 0;
  events.push({ title: event.event_name, start: cur.toISOString().split("T")[0], end: masterEnd.toISOString().split("T")[0], startTime: event.start_time, endTime: event.end_time, startAt: event.start_at, endAt: event.end_at, scheduleZone: event.schedule_time_zone, eventId: event.id, isRecurring: true, instanceNumber: 1 });
  n = 1;
  while (n < (count || 365)) {
    let next: Date;
    switch (event.frequency) {
      case "daily": next = addDays(cur, interval); break;
      case "weekly":
        if (event.recurrence_days_of_week?.length > 0) {
          const days = event.recurrence_days_of_week.sort();
          const d = cur.getUTCDay();
          const nd = days.find((x: number) => x > d);
          if (nd !== undefined) { next = addDays(cur, nd - d); }
          else { next = addDays(cur, 7 - d + (interval - 1) * 7 + days[0]); }
        } else { next = addWeeks(cur, interval); }
        break;
      case "monthly":
        next = addMonths(cur, interval);
        if (event.recurrence_day_of_month) {
          const td = Math.min(event.recurrence_day_of_month, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate());
          next.setDate(td);
        }
        break;
      default: return events;
    }
    if (recEnd && isAfter(next, recEnd)) break;
    if (count && n >= count) break;
    const ne = new Date(next.getTime() + durationMs);
    events.push({ title: event.event_name, start: next.toISOString().split("T")[0], end: ne.toISOString().split("T")[0], startTime: event.start_time, endTime: event.end_time, scheduleZone: event.schedule_time_zone, eventId: event.id, isRecurring: true, instanceNumber: n + 1 });
    cur = next; n++;
    if (n > 1000) break;
  }
  return events;
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const EventTooltip = ({ event, position, visible }: { event: any; position: { x: number; y: number }; visible: boolean }) => {
  if (!visible || !event) return null;
  const type   = event.extendedProps?.type || "event";
  const theme  = getEventTheme(type);
  const start  = dayjs(event.start).format("MMM DD, YYYY · hh:mm A");
  const end    = event.end ? dayjs(event.end).format("hh:mm A") : "";

  return (
    <Paper elevation={0} sx={{
      position: "fixed", left: position.x + 12, top: position.y - 8,
      zIndex: 9999, width: 280, p: "12px 14px",
      borderRadius: "10px", border: `1px solid ${theme.border}`,
      boxShadow: "0 12px 28px rgba(15,23,42,0.13)", pointerEvents: "none", bgcolor: "#fff",
      "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, borderRadius: "10px 10px 0 0", bgcolor: theme.text },
    }}>
      <Stack direction="row" alignItems="flex-start" spacing={0.75} mb={1}>
        <Box sx={{ color: theme.text, mt: "1px", "& svg": { fontSize: 13 } }}>{getTypeIcon(type, 13)}</Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy, lineHeight: 1.3 }}>
          {event.title.replace(/^(Event:|Assessment:|Interview:|Placement:)\s*/, "")}
        </Typography>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "64px 1fr", gap: "4px 8px" }}>
        {[
          event.extendedProps?.status && ["Status", event.extendedProps.status.charAt(0).toUpperCase() + event.extendedProps.status.slice(1)],
          ["Start",   start + (end ? ` – ${end}` : "")],
          event.extendedProps?.company  && ["Company",  event.extendedProps.company],
          event.extendedProps?.salary   && ["Salary",   `₹${event.extendedProps.salary.toLocaleString()}`],
          event.extendedProps?.code     && ["Code",     event.extendedProps.code],
          event.extendedProps?.questionCount && ["Questions", event.extendedProps.questionCount],
        ].filter(Boolean).map(([label, value]: any, i: number) => (
          <>
            <Typography key={`l${i}`} sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8" }}>{label}</Typography>
            <Typography key={`v${i}`} sx={{ fontSize: 10.5, color: "#1e293b" }}>{value}</Typography>
          </>
        ))}
      </Box>
    </Paper>
  );
};

// ─── Notification card ────────────────────────────────────────────────────────
const NotifCard = ({ item, index, expanded, onToggle }: { item: NotificationItem; index: number; expanded: boolean; onToggle: () => void }) => {
  const isNew = index < 2;
  return (
    <Box sx={{
      p: "10px 12px", borderRadius: "8px",
      border: `1px solid ${isNew ? C.borderBlue : C.border}`,
      bgcolor: isNew ? "#f8fbff" : "#fff",
      transition: "border-color .15s, box-shadow .15s",
      "&:hover": { borderColor: alpha(C.navy, 0.25), boxShadow: "0 2px 8px rgba(15,23,42,0.06)" },
    }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{
          mt: "1px", width: 26, height: 26, borderRadius: "7px", flexShrink: 0,
          display: "grid", placeItems: "center",
          bgcolor: isNew ? alpha(C.navy, 0.08) : "#f1f5f9",
          color: isNew ? C.navy : "#94a3b8",
          "& svg": { fontSize: 13 },
        }}>
          <NotificationsActiveOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy, lineHeight: 1.3, pr: 0.5 }}>
              {item.title}
            </Typography>
            {isNew && (
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: C.assessment, flexShrink: 0, mt: "4px" }} />
            )}
          </Stack>
          <Box sx={{
            mt: "4px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: expanded ? "none" : 2,
            WebkitBoxOrient: "vertical",
          }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
              {item.description}
            </Typography>
          </Box>
          {item.description.length > 80 && (
            <Box
              component="button"
              onClick={onToggle}
              sx={{
                mt: "4px", border: "none", background: "none", cursor: "pointer",
                p: 0, display: "flex", alignItems: "center", gap: "2px",
                color: C.assessment, fontSize: 10, fontWeight: 600,
                "&:hover": { opacity: 0.75 },
              }}
            >
              {expanded ? <><ExpandLessIcon sx={{ fontSize: 12 }} /> Less</> : <><ExpandMoreIcon sx={{ fontSize: 12 }} /> More</>}
            </Box>
          )}
          {item.created_at && (
            <Typography sx={{ mt: "3px", fontSize: 10, color: "#94a3b8" }}>
              {dayjs(item.created_at).format("MMM DD · hh:mm A")}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

// ─── Main Calendar page ───────────────────────────────────────────────────────
function Calendar() {
  const { user } = useUser();
  const userId    = user?.studentId;
  const userEmail = user?.email;

  const [data,         setData]         = useState<CalendarItem[]>([]);
  const [notification, setNotification] = useState<NotificationItem[]>([]);
  const [expandedSet,  setExpandedSet]  = useState<Set<number>>(new Set());
  const [currentView,  setCurrentView]  = useState("dayGridMonth");
  const [calTitle,     setCalTitle]     = useState(dayjs().format("MMMM YYYY"));
  const [tooltip, setTooltip] = useState<{ visible: boolean; event: any; position: { x: number; y: number } }>({
    visible: false, event: null, position: { x: 0, y: 0 },
  });

  const calRef = useRef<FullCalendar>(null);

  // ── Fetch calendar data ──────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !userEmail) return;
    const fetch = async () => {
      try {
        const [evR, asR, inR, plR] = await Promise.all([
          Axios.get(`/api/placement/events/${userId}`),
          Axios.get(`/api/integration/external-service/assessment`, { params: { email: userEmail } }),
          Axios.get(`/api/integration/external-service/interview?email=${userEmail}`),
          Axios.get(`/api/placement/${userId}`),
        ]);

        const accepted = evR.data.result.filter((e: any) => e.audience_status === "accepted");
        const evInst: any[] = [];
        for (const e of accepted) if (!e.parent_event_id) evInst.push(...generateRecurringEvents(e));
        const eventsData: CalendarItem[] = evInst.map(i => ({ title: i.title, start: studentScheduleWall(i.startAt, i.start, i.startTime, i.scheduleZone) || i.start, end: studentScheduleWall(i.endAt, i.end, i.endTime, i.scheduleZone) || i.end, type: "event", extendedProps: { status: "accepted", eventId: i.eventId, isRecurring: i.isRecurring, instanceNumber: i.instanceNumber } }));

        const assessData: CalendarItem[] = asR.data.result.filter((i: any) => i.startAt || i.startDate).map((i: any) => ({ title: `Assessment: ${i.name}`, start: studentScheduleWall(i.startAt, i.startDate, i.startTime) || i.startDate, end: studentScheduleWall(i.endAt, i.endDate, i.endTime) || i.endDate, type: "assessment", extendedProps: { status: i.status, code: i.code, questionCount: i.questionCount, score: i.score, totalScore: i.totalScore } }));

        const interData: CalendarItem[] = inR.data.result.filter((i: any) => i.startAt || i.startDate).map((i: any) => ({ title: `Interview: ${i.name}`, start: studentScheduleWall(i.startAt, i.startDate, i.startTime) || i.startDate, end: studentScheduleWall(i.endAt, i.endDate, i.endTime) || i.endDate, type: "interview", extendedProps: { status: i.status, code: i.code, questionCount: i.questionCount, score: i.score, totalScore: i.totalScore } }));

        const allowed = new Set(["accepted", "offered", "placed", "attended", "rejected"]);
        const placData: CalendarItem[] = plR.data.result.flatMap((p: any) =>
          p.jobs.filter((j: any) => allowed.has(j.status)).map((j: any): CalendarItem => ({
            title: `${p.title} - ${j.title}`, start: new Date(p.start_date).toISOString().split("T")[0], end: new Date(p.end_date).toISOString().split("T")[0],
            type: "placement", extendedProps: { status: j.status, company: p.company?.name, salary: j.salary, placementType: p.type, priority: 1 },
          }))
        );

        setData([...eventsData, ...assessData, ...interData, ...placData].sort((a, b) => (b.extendedProps.priority || 0) - (a.extendedProps.priority || 0)));
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [userId, userEmail]);

  // ── Fetch notifications (same logic as original DashboardReportPage) ─────
  useEffect(() => {
    if (!userId) return;
    const timer = setTimeout(async () => {
      try {
        const res = await Axios.get(`/api/notification/${userId}`);
        const publishedAndSorted = (res.data.result as NotificationItem[])
          .filter(n => n.status === "published")
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setNotification(publishedAndSorted);
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [userId]);

  // ── Derived events ───────────────────────────────────────────────────────
  const fcEvents = useMemo(() => data.map((e, i) => {
    let start: string, end: string;
    if (e.type === "placement") { start = `${e.start}T00:00:00`; end = `${e.end}T23:59:59`; }
    else if (e.startTime && e.endTime) { start = `${e.start}T${e.startTime}`; end = `${e.end}T${e.endTime}`; }
    else { start = e.start; end = e.end; }
    return { id: `${e.type}-${i}`, title: e.title, start, end, allDay: e.type === "placement", backgroundColor: "transparent", borderColor: "transparent", extendedProps: { type: e.type, ...e.extendedProps } };
  }), [data]);

  // ── Calendar navigation ──────────────────────────────────────────────────
  const nav = (action: "prev" | "next" | "today") => {
    const api = calRef.current?.getApi();
    if (!api) return;
    api[action]();
    setCalTitle(api.view.title);
  };
  const switchView = (v: "dayGridMonth" | "timeGridWeek") => {
    const api = calRef.current?.getApi();
    if (!api) return;
    api.changeView(v);
    setCurrentView(v);
    setCalTitle(api.view.title);
  };

  // ── Render event ─────────────────────────────────────────────────────────
  const renderEvent = useCallback((info: any) => {
    const ev    = info.event;
    const type  = ev.extendedProps?.type || "event";
    const th    = getEventTheme(type);
    const clean = ev.title.replace(/^(Event:|Assessment:|Interview:|Placement:)\s*/, "");
    const sub   = type === "placement"
      ? ev.extendedProps?.company || ev.extendedProps?.status || ""
      : ev.end ? `${dayjs(ev.start).format("hh:mm")} – ${dayjs(ev.end).format("hh:mm A")}` : dayjs(ev.start).format("hh:mm A");
    const isMonth = currentView === "dayGridMonth";

    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "stretch" }}>
        <Box sx={{ width: 2, borderRadius: 99, bgcolor: th.text, mr: "4px", flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0, px: "4px", py: isMonth ? "2px" : "4px", borderRadius: "5px", bgcolor: th.soft, border: `1px solid ${th.border}`, overflow: "hidden" }}>
          <Stack direction="row" alignItems="center" spacing={0.3}>
            <Box sx={{ color: th.text, flexShrink: 0, "& svg": { fontSize: 8 } }}>{getTypeIcon(type, 8)}</Box>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMonth ? "nowrap" : "normal", display: "-webkit-box", WebkitLineClamp: isMonth ? 1 : 2, WebkitBoxOrient: "vertical", lineHeight: 1.2 }}>
              {clean}
            </Typography>
          </Stack>
          {sub && (
            <Typography sx={{ fontSize: 8, color: alpha(th.text, 0.65), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: "1px", lineHeight: 1.2 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }, [currentView]);

  return (
    <PageShell>
      {/* ── Page header ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", letterSpacing: "-0.01em" }}>
            {t`Calendar`}
          </Typography>
        </Box>
      </Stack>

      {/* ── Main layout: calendar (left) + notifications (right) ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 2, alignItems: "start" }}>

        {/* ── Left: Calendar ── */}
        <Stack spacing={1.5}>
          {/* Calendar toolbar */}
          <SectionCard elevation={0} sx={{ px: 2, py: 1.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {/* Nav */}
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <IconButton onClick={() => nav("prev")} size="small" sx={{ width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: "8px", bgcolor: "#fff", "&:hover": { bgcolor: alpha(C.navy, 0.05) } }}>
                  <ChevronLeftRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Button
                  startIcon={<CalendarTodayRoundedIcon sx={{ fontSize: 13 }} />}
                  onClick={() => nav("today")}
                  sx={{ textTransform: "none", borderRadius: "8px", px: 1.5, py: 0.5, minHeight: 30, width: 180, border: `1px solid ${C.border}`, color: C.navy, fontWeight: 700, fontSize: 12, bgcolor: "#fff", "&:hover": { bgcolor: alpha(C.navy, 0.04) } }}
                >
                  {calTitle}
                </Button>
                <IconButton onClick={() => nav("next")} size="small" sx={{ width: 30, height: 30, border: `1px solid ${C.border}`, borderRadius: "8px", bgcolor: "#fff", "&:hover": { bgcolor: alpha(C.navy, 0.05) } }}>
                  <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>

              {/* View switcher */}
              <Box sx={{ p: "3px", borderRadius: "10px", bgcolor: "#f1f5f9", border: `1px solid ${C.border}`, display: "flex", gap: "3px" }}>
                {(["dayGridMonth", "timeGridWeek"] as const).map(v => {
                  const active = currentView === v;
                  return (
                    <Button key={v} onClick={() => switchView(v)} sx={{
                      textTransform: "none", minWidth: 72, height: 28, borderRadius: "7px", fontWeight: 700, fontSize: 11,
                      bgcolor: active ? C.navy : "transparent", color: active ? "#fff" : "#64748b",
                      boxShadow: active ? "0 1px 4px rgba(16,45,77,0.25)" : "none",
                      "&:hover": { bgcolor: active ? C.navyLight : alpha(C.navy, 0.06), boxShadow: "none" },
                      transition: "all .15s",
                    }}>
                      {v === "dayGridMonth" ? "Month" : "Week"}
                    </Button>
                  );
                })}
              </Box>
            </Stack>
          </SectionCard>

          {/* Calendar grid */}
          <SectionCard elevation={0} sx={{ p: 1.5 }}>
            <Box sx={{
              "& .fc": { fontFamily: `"DM Sans","Segoe UI",sans-serif`, fontSize: "11px", "--fc-border-color": "#e2e8f0", "--fc-today-bg-color": "#f0f7ff" },
              "& .fc-toolbar": { display: "none" },
              "& .fc-scrollgrid": { borderRadius: "10px", overflow: "hidden", border: "1px solid #e2e8f0" },
              "& .fc-col-header-cell": { bgcolor: "#f8fafc", py: "5px" },
              "& .fc-col-header-cell-cushion": { fontSize: "10px !important", fontWeight: "700 !important", color: "#64748b !important", textTransform: "uppercase", letterSpacing: "0.05em", textDecoration: "none !important" },
              "& .fc-daygrid-day-number": { fontSize: "11px !important", fontWeight: "600 !important", color: "#64748b !important", padding: "5px 7px !important", textDecoration: "none !important" },
              "& .fc-day-today .fc-daygrid-day-number": { bgcolor: `${C.navy} !important`, color: "#fff !important", borderRadius: "6px", px: "7px", py: "3px" },
              "& .fc-day-today": { bgcolor: "#f8fbff !important" },
              "& .fc-timegrid-slot": { height: "38px" },
              "& .fc-timegrid-slot-label-cushion": { fontSize: "9px !important", fontWeight: "600 !important", color: "#94a3b8 !important" },
              "& .fc-timegrid-now-indicator-line": { borderColor: "#ef4444 !important" },
              "& .fc-event": { border: "none !important", borderRadius: "6px !important" },
              "& .fc-event .fc-event-main": { padding: "0 !important", height: "100%" },
              "& .fc-daygrid-event": { margin: "1px 2px !important", border: "none !important", background: "transparent !important" },
              "& .fc-timegrid-event": { border: "none !important", background: "transparent !important" },
              "& .fc-more-link": { fontSize: "9px !important", fontWeight: "700 !important", color: `${C.navy} !important`, bgcolor: `${alpha(C.navy, 0.07)} !important`, borderRadius: "4px !important", px: "4px", mt: "1px" },
              "& .fc-popover": { borderRadius: "10px !important", border: "1px solid #dbeafe !important", boxShadow: "0 8px 24px rgba(15,23,42,0.12) !important", overflow: "hidden !important" },
              "& .fc-popover-header": { bgcolor: `${C.navy} !important`, color: "#fff !important", fontWeight: "700 !important", fontSize: "11px !important", py: "7px !important", px: "10px !important" },
              "& .fc-popover-body": { maxHeight: "200px !important", overflowY: "auto !important", p: "6px !important", bgcolor: "#fff !important" },
              "& .fc table, & .fc th, & .fc td": { borderColor: "#e2e8f0 !important" },
            }}>
              <FullCalendar
                ref={calRef}
                height="auto"
                aspectRatio={1.5}
                plugins={[dayGridPlugin, timeGridPlugin]}
                initialView="dayGridMonth"
                events={fcEvents}
                eventContent={renderEvent}
                dayMaxEvents={2}
                moreLinkText={n => `+${n} more`}
                moreLinkClick="popover"
                headerToolbar={false}
                views={{
                  dayGridMonth: { titleFormat: { year: "numeric", month: "long" }, dayMaxEventRows: 2 },
                  timeGridWeek: { slotMinTime: "01:00:00", slotMaxTime: "24:00:00", allDaySlot: true, titleFormat: { year: "numeric", month: "long", day: "numeric" }, eventMinHeight: 26, slotEventOverlap: false },
                }}
                datesSet={arg => { setCurrentView(arg.view.type); setCalTitle(arg.view.title); }}
                timeZone="local"
                eventMouseEnter={info => {
                  const rect = info.el.getBoundingClientRect();
                  setTooltip({ visible: true, event: info.event, position: { x: rect.left + rect.width / 2, y: rect.top } });
                }}
                eventMouseLeave={() => setTooltip({ visible: false, event: null, position: { x: 0, y: 0 } })}
                eventDisplay="block"
                eventOrder={(a: any, b: any) => {
                  const at = a.extendedProps?.type || "event", bt = b.extendedProps?.type || "event";
                  if (at === "placement" && bt !== "placement") return -1;
                  if (bt === "placement" && at !== "placement") return 1;
                  return a.title.localeCompare(b.title);
                }}
              />
            </Box>
          </SectionCard>
        </Stack>

        {/* ── Right: Notifications ── */}
        <Stack spacing={1.5} sx={{ position: "sticky", top: 16 }}>
          {/* Notifications header card */}
          <SectionCard elevation={0} sx={{ px: 2, py: 1.25 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 28, height: 28, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: alpha(C.navy, 0.07), color: C.navy, "& svg": { fontSize: 14 } }}>
                  <NotificationsActiveOutlinedIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy }}>
                    {t`Notifications`}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                    {notification.length} {t`updates`}
                  </Typography>
                </Box>
              </Stack>
              {notification.length > 0 && (
                <Badge badgeContent={Math.min(notification.length, 9)} color="primary" sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 16, minWidth: 16 } }}>
                  <Box sx={{ width: 8, height: 8 }} />
                </Badge>
              )}
            </Stack>
          </SectionCard>

          {/* Notifications list */}
          <SectionCard elevation={0} sx={{ overflow: "hidden" }}>
            <Box sx={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto", p: 1.25,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { bgcolor: "#f8fafc" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 99 },
            }}>
              {notification.length === 0 ? (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: "16px", display: "grid", placeItems: "center", bgcolor: "#f1f5f9", color: "#94a3b8", "& svg": { fontSize: 24 } }}>
                    <NotificationsNoneOutlinedIcon />
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                      {t`All caught up`}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.25 }}>
                      {t`No notifications yet`}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {notification.map((item, i) => (
                    <NotifCard
                      key={i}
                      item={item}
                      index={i}
                      expanded={expandedSet.has(i)}
                      onToggle={() => setExpandedSet(prev => {
                        const n = new Set(prev);
                        n.has(i) ? n.delete(i) : n.add(i);
                        return n;
                      })}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </SectionCard>
        </Stack>
      </Box>

      <EventTooltip event={tooltip.event} position={tooltip.position} visible={tooltip.visible} />
    </PageShell>
  );
}

export default Calendar;
